import { TableColumn } from "react-data-table-component";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Currency } from "../../../Model/Setting/Currency";
import {
  CURRENCY_ADD,
  CURRENCY_ARCHIVE,
  CURRENCY_EDIT,
  CURRENCY_LIST,
  CURRENCY_TITLE,
} from "../../../Routes/Setting/Currency";
import { fetchData } from "../../../Util/OrionList";
import CustomEditButton from "../../../Components/Button/CustomEditButton";
import CustomDataTable from "../../../Components/CustomDataTable";
import { setToast } from "../../../Util/Toast";
import { Breadcrumb, Col, Row } from "react-bootstrap";
import CustomFilter, { InputValues } from "../../../Components/CustomFilter";
import { FilterOperator } from "@tailflow/laravel-orion/lib/drivers/default/enums/filterOperator";
import CustomButton from "../../../Components/Button/CustomButton";
import pluralize from "pluralize";
import CustomRestoreIconButton from "../../../Components/Button/CustomRestoreIconButton";
import CustomArchiveButton from "../../../Components/Button/CustomArchiveButton";
import CustomArchiveModal from "../../../Components/CustomArchiveModal";
import CustomRestoreModal from "../../../Components/CustomRestoreModal";

interface CurrencyItem {
  id: number;
  code: string;
  symbol: string;
  created_at: string;
  updated_at: string;
}

function List({ permissions }: any) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [data, setData] = useState<CurrencyItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [limit, setLimit] = useState(
    Number(searchParams.get("perPage")) === 0
      ? 10
      : Number(searchParams.get("perPage")),
  );
  const [page, setPage] = useState(
    Number(searchParams.get("page")) === 0
      ? 1
      : Number(searchParams.get("page")),
  );
  const navigate = useNavigate();
  const [inputValues, setInputValues] = useState<InputValues>({});
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [sortField, setSortField] = useState<string>("id");
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [showArchiveModal, setshowArchiveModal] = useState(false);
  const [ArchiveItemId, setArchiveItemId] = useState<number | null>(null);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [isArchive, setIsArchive] = useState<boolean>(
    location.pathname === CURRENCY_ARCHIVE,
  );
  const [showRestoreModal, setshowRestoreModal] = useState(false);
  const [restoreItemId, setRestoreItemId] = useState<number | null>(null);

  const handleRowEditButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: number,
  ) => {
    e.preventDefault();
    navigate(CURRENCY_EDIT.replace(":id", id.toString()));
  };

  const handleRowArchiveButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: number,
  ) => {
    e.preventDefault();
    setArchiveItemId(id);
    setshowArchiveModal(true);
  };
  const handleRowCreateButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    navigate(CURRENCY_ADD);
  };

  const handleRowRestoreButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    id: number,
  ) => {
    e.preventDefault();
    setRestoreItemId(id);
    setshowRestoreModal(true);
  };

  const handleArchiveButtonClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    navigate(CURRENCY_ARCHIVE);
  };

  const columns: TableColumn<CurrencyItem>[] = [
    {
      name: "Id",
      cell: (row) => row.id,
    },
    {
      name: "Currency Code",
      cell: (row) => row.code,
    },
    {
      name: "Symbol",
      cell: (row) => row.symbol,
    },
    {
      name: "Status",
      cell: (row: any) => (
        <div>
          <p
            className={row.active === 1 ? "text-success" : "text-danger"}
            style={{ fontWeight: "bold", margin: "0" }}
          >
            {row.active === 1 ? "Active" : "Inactive"}
          </p>
        </div>
      ),
    },
    {
      name: "Deleted At",
      cell: (row: any) => row.deleted_at,
      omit: !isArchive,
    },
    {
      name: "Actions",
      button: true,
      omit: permissions.update && !permissions.delete,
      cell: (row) => (
        <div>
          {isArchive ? (
            permissions.restore && (
              <CustomRestoreIconButton
                id={row.id}
                onClick={handleRowRestoreButtonClick}
              />
            )
          ) : (
            <>
              {permissions.update && (
                <CustomEditButton
                  id={row.id}
                  onClick={handleRowEditButtonClick}
                />
              )}
              {permissions.delete && (
                <CustomArchiveButton
                  id={row.id}
                  onClick={handleRowArchiveButtonClick}
                />
              )}
            </>
          )}
        </div>
      ),
    },
  ];

  // @ts-ignore
  const fetchCurrency = async () => {
    try {
      await fetchData(
        page,
        limit,
        inputValues,
        setLoading,
        setData,
        setTotalRows,
        navigate,
        new Currency(),
        sortField,
        sortOrder,
        isArchive,
      );
    } catch (error: any) {
      setToast("error", error.response.data.message);
    }
  };

  const handlePerRowsChange = async (rowLimit: any, page: any) => {
    if (limit === rowLimit) {
      setLimit(rowLimit);
      setPage(page);
    } else {
      setLimit(rowLimit);
      setPage(1);
    }
  };

  useEffect(() => {
    setSortField("id");
    setSortOrder("desc");
  }, []);

  const handlePageChange = (page: any) => {
    setPage(page);
  };
  useEffect(() => {
    fetchCurrency();
  }, [limit, page]); // eslint-disable-line
  useEffect(() => {
    if (initialFetchDone) {
      setPage(1);
      fetchCurrency();
    }
  }, [isArchive]); // eslint-disable-line

  useEffect(() => {
    setIsArchive(location.pathname === CURRENCY_ARCHIVE);
    setInitialFetchDone(true);
  }, [location]);

  const handleGoBack = () => {
    navigate(CURRENCY_LIST);
  };

  const filterOptions: any = [
    {
      id: "id",
      value: "id",
      label: "Id",
      fieldType: "text",
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
        Contains: FilterOperator.Like,
        NotContain: FilterOperator.NotLike,
      },
    },
    {
      id: "code",
      value: "code",
      label: "Currency Code",
      fieldType: "text",
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
        Contains: FilterOperator.Like,
        NotContain: FilterOperator.NotLike,
      },
    },
    {
      id: "active",
      value: "active",
      label: "Status",
      fieldType: "text",
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
      },
    },
    {
      id: "symbol",
      value: "symbol",
      label: "Symbol",
      fieldType: "text",
      allowedFilter: {
        Exact: FilterOperator.Equal,
        NotExact: FilterOperator.NotEqual,
      },
    },
  ];

  return (
    <>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        {isArchive ? (
          <>
            <Breadcrumb.Item onClick={handleGoBack}>Currency</Breadcrumb.Item>
            <Breadcrumb.Item active className="fw-bold">
              Archive
            </Breadcrumb.Item>
          </>
        ) : (
          <>
            <Breadcrumb.Item active className="fw-bold">
              Currency
            </Breadcrumb.Item>
          </>
        )}
      </Breadcrumb>
      <Row className="align-items-center">
        <Col className="col-10">
          <div className="d-flex justify-content-start">
            <div style={{ display: "inline-block" }}>
              <h6 className="mt-1">
                {pluralize(CURRENCY_TITLE)} {isArchive ? "Archive" : ""}
              </h6>
            </div>
            <CustomFilter
              options={filterOptions}
              inputValues={inputValues}
              setInputValues={setInputValues}
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              getData={fetchCurrency}
              setPage={setPage}
            />
          </div>
        </Col>
        <Col className="col-2 text-xxl-end text-xl-end text-lg-end text-md-start text-sm-start text-xs-start">
          {isArchive ? (
            <CustomButton
              label={`Back`}
              variant="secondary"
              onClick={handleGoBack}
            />
          ) : (
            <>
              {permissions.add && (
                <CustomButton
                  label={`Add`}
                  variant="success"
                  onClick={handleRowCreateButtonClick}
                />
              )}
              {permissions.delete && (
                <CustomButton
                  label={`Archive`}
                  variant="danger"
                  onClick={handleArchiveButtonClick}
                  customeClass="ms-2"
                />
              )}
            </>
          )}
        </Col>
      </Row>
      <div className="mb-4">
        <CustomDataTable
          columns={columns}
          data={data}
          progressPending={loading}
          paginationTotalRows={totalRows}
          onChangeRowsPerPage={handlePerRowsChange}
          onChangePage={handlePageChange}
          handleRowCreateButtonClick={handleRowCreateButtonClick}
          currentPages={page}
          currentRowsPerPage={limit}
          module={"Currency"} // Pass optional fields to CustomDataTable
        />
      </div>
      <CustomArchiveModal
        title={CURRENCY_TITLE}
        model={Currency}
        getData={fetchCurrency}
        ArchiveItemId={ArchiveItemId}
        setArchiveItemId={setArchiveItemId}
        showArchiveModal={showArchiveModal}
        setshowArchiveModal={setshowArchiveModal}
      />
      <CustomRestoreModal
        title={CURRENCY_TITLE}
        model={Currency}
        getData={fetchCurrency}
        RestoreItemId={restoreItemId}
        showRestoreModal={showRestoreModal}
        setRestoreItemId={setRestoreItemId}
        setshowRestoreModal={setshowRestoreModal}
      />
    </>
  );
}

export default List;
